/**
 * Hook for managing the browser's PushSubscription against the backend.
 *
 * Behaviour summary:
 *   - `status` is one of:
 *       "unsupported"          (no SW or Notification API in this browser)
 *       "disabled-by-server"   (VAPID_PUBLIC_KEY missing on server)
 *       "blocked"              (user denied notification permission)
 *       "default"              (permission not yet asked)
 *       "subscribed"           (active subscription exists in the browser)
 *       "loading"
 *   - `enable()` asks for permission, calls PushManager.subscribe(...),
 *     and POSTs the result to /api/push/subscribe.
 *   - `disable()` calls subscription.unsubscribe() AND DELETE /api/push/subscribe.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function usePushSubscription() {
  const [status, setStatus] = useState("loading");
  const [vapidKey, setVapidKey] = useState("");
  const [serverEnabled, setServerEnabled] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setStatus("unsupported"); return;
    }
    try {
      const { data } = await api.get("/push/vapid-public-key");
      setVapidKey(data.public_key || "");
      setServerEnabled(!!data.enabled);
      if (!data.enabled || !data.public_key) {
        setStatus("disabled-by-server");
        return;
      }
    } catch (_) {
      setStatus("disabled-by-server"); return;
    }
    const perm = Notification.permission;
    if (perm === "denied") { setStatus("blocked"); return; }
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) setStatus("subscribed");
      else setStatus(perm === "granted" ? "default" : perm);
    } catch (_) {
      setStatus("default");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    setStatus("loading");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus(perm === "denied" ? "blocked" : "default"); return; }
      const { data: keyResp } = await api.get("/push/vapid-public-key");
      if (!keyResp.public_key) { setStatus("disabled-by-server"); return; }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
        });
      }
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
        label: deviceLabel(),
      });
      setStatus("subscribed");
    } catch (e) {
      console.error("push enable failed", e);
      setStatus(Notification.permission === "denied" ? "blocked" : "default");
    }
  }, []);

  const disable = useCallback(async () => {
    setStatus("loading");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); } catch (_) { /* tolerate */ }
        try { await sub.unsubscribe(); } catch (_) { /* tolerate */ }
      }
      setStatus(Notification.permission === "granted" ? "default" : Notification.permission);
    } catch (_) {
      await refresh();
    }
  }, [refresh]);

  const sendTest = useCallback(async () => {
    try { await api.post("/push/test"); return true; }
    catch (_) { return false; }
  }, []);

  return { status, vapidKey, serverEnabled, enable, disable, sendTest, refresh };
}

function deviceLabel() {
  try {
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
    if (/Android/i.test(ua)) return "Android";
    if (/Mac OS X/i.test(ua)) return "Mac";
    if (/Windows/i.test(ua)) return "Windows";
    if (/Linux/i.test(ua)) return "Linux";
  } catch (_) { /* ignore detection errors */ }
  return "Browser";
}
